import { useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * Minimal accordion section, closed by default.
 * Props:
 *  - title: string (main label)
 *  - subtitle: optional smaller line under the title
 *  - icon: optional lucide icon component
 *  - count: optional number pill (e.g. how many items inside)
 *  - defaultOpen: boolean
 *  - testId: data-testid prefix
 *  - accent: optional accent class for the left border
 *  - rightSlot: optional ReactNode shown on the right of the header (above the chevron)
 */
export default function Accordion({
    title,
    subtitle,
    icon: Icon,
    count,
    defaultOpen = false,
    testId,
    accent,
    rightSlot,
    children,
}) {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <section
            className={`panel overflow-hidden ${accent ? `border-l-4 ${accent}` : ""}`}
            data-testid={testId ? `${testId}-section` : undefined}
        >
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                className="w-full flex items-center gap-3 px-4 sm:px-5 py-4 text-left hover:bg-surface transition-colors"
                data-testid={testId ? `${testId}-toggle` : undefined}
            >
                {Icon && (
                    <div className="w-9 h-9 bg-orange/15 flex items-center justify-center shrink-0">
                        <Icon size={16} className="text-orange" strokeWidth={1.8} />
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="h-display text-sm sm:text-base font-semibold leading-tight truncate">{title}</h2>
                        {typeof count === "number" && (
                            <span className="pill bg-ink text-white !py-0 !px-2 text-[10px]" data-testid={testId ? `${testId}-count` : undefined}>
                                {count}
                            </span>
                        )}
                    </div>
                    {subtitle && <div className="text-[11px] text-muted2 mt-0.5 truncate">{subtitle}</div>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {rightSlot}
                    <ChevronDown
                        size={16}
                        className={`text-muted2 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                    />
                </div>
            </button>
            {open && (
                <div
                    className="px-4 sm:px-5 pb-5 pt-1 border-t border-rule"
                    data-testid={testId ? `${testId}-content` : undefined}
                >
                    {children}
                </div>
            )}
        </section>
    );
}
